import axios from '../api/api' // Configured Axios instance

// NEW BACKEND ENDPOINT BASE
const CART_BASE = '/orders/cart'

// Check if the user is authenticated
const isAuthenticated = () => !!localStorage.getItem('authToken') || !!localStorage.getItem('token') || !!localStorage.getItem('access_token')

// Get or create a session key for the cart
const getOrCreateSessionKey = () => {
  let key = localStorage.getItem('cart_session_key')
  if (!key) {
    key = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
    localStorage.setItem('cart_session_key', key)
  }
  return key
}

// Apply Promo Code
export const applyCoupon = async couponCode => {
  try {
    const payload = {
      code: couponCode?.trim() || couponCode
    }

    // Add session key for non-authenticated users
    if (!isAuthenticated()) {
      payload.session_key = getOrCreateSessionKey()
    }

    const response = await axios.post(`${CART_BASE}/promo-code/apply/`, payload)
    return {
      success: response.data?.success ?? true,
      message: response.data?.message || 'Promo code applied successfully',
      promo_code_info: response.data?.promo_code_info,
      discount_amount: response.data?.discount_amount,
      cart: response.data?.cart
    }
  } catch (error) {
    // console.error('❌ Error applying promo code:', error.response?.data || error)
    throw error
  }
}

// Remove Promo Code
export const removeCoupon = async () => {
  try {
    const payload = {}

    // Add session key for non-authenticated users
    if (!isAuthenticated()) {
      payload.session_key = getOrCreateSessionKey()
    }

    const response = await axios.post(`${CART_BASE}/promo-code/remove/`, payload)
    return {
      success: response.data?.success ?? true,
      message: response.data?.message || 'Promo code removed',
      cart: response.data?.cart
    }
  } catch (error) {
    // console.error('❌ Error removing promo code:', error.response?.data || error)
    throw error
  }
}

// Add Item to Cart (supports variant + guest session)
export const addItemToCart = async ({ productId, quantity = 1, specialInstructions = '', variantId = null, force = false, sessionKey = null }) => {
  try {
    const payload = {
      product_id: productId,
      quantity,
      special_instructions: specialInstructions || ''
    }
    if (variantId) payload.variant_id = variantId
    if (force) payload.force = true

    // Always get/create session key for non-authenticated users
    if (!isAuthenticated()) {
      sessionKey = sessionKey || getOrCreateSessionKey()
      payload.session_key = sessionKey
    }

    const response = await axios.post(`${CART_BASE}/add/`, payload)

    const normalizedResponse = normalizeAddOrUpdateResponse(response.data)

    // Include grocery_store_notice if present (for closed stores that still allow orders)
    if (response.data?.grocery_store_notice) {
      normalizedResponse.grocery_store_notice = response.data.grocery_store_notice
    }

    return normalizedResponse
  } catch (error) {
    console.error('❌ Error adding item to cart:', error.response?.data || error)
    throw error
  }
}

// Update Cart Item by item_id
export const updateCartItemOnBackend = async (itemId, { quantity, variantId, specialInstructions, sessionKey = null }) => {
  try {
    // Get session key for anonymous users FIRST
    if (!isAuthenticated()) {
      sessionKey = sessionKey || getOrCreateSessionKey()
    }

    // Validate itemId
    if (!itemId || itemId.toString().startsWith('temp-') || itemId.toString().startsWith('optimistic-')) {
      console.warn('⚠️ Cannot update item with temporary/optimistic ID:', itemId)
      const freshCart = await getCartFromBackend(sessionKey) // PASS SESSION KEY!
      return {
        success: true,
        message: 'Cart refreshed',
        cart: freshCart,
        cart_total: freshCart.total_amount,
        cart_items_count: freshCart.total_items
      }
    }

    const payload = {}
    if (quantity !== undefined) payload.quantity = quantity
    if (variantId !== undefined) payload.variant_id = variantId
    if (specialInstructions !== undefined) payload.special_instructions = specialInstructions

    // Add session key for anonymous users
    if (sessionKey) {
      payload.session_key = sessionKey
    }

    const response = await axios.patch(`${CART_BASE}/items/${itemId}/`, payload)
    return normalizeAddOrUpdateResponse(response.data)
  } catch (error) {
    console.error('❌ Error updating cart item:', error.response?.data || error)
    throw error
  }
}

// Remove Cart Item by item_id
export const removeItemFromCart = async (itemId, sessionKey = null) => {
  try {
    // Get session key for anonymous users FIRST
    if (!isAuthenticated()) {
      sessionKey = sessionKey || getOrCreateSessionKey()
    }

    // Validate itemId
    if (!itemId || itemId.toString().startsWith('temp-') || itemId.toString().startsWith('optimistic-')) {
      console.warn('⚠️ Cannot remove item with temporary/optimistic ID:', itemId)
      const freshCart = await getCartFromBackend(sessionKey) // PASS SESSION KEY!
      return {
        success: true,
        message: 'Cart refreshed',
        cart_total: freshCart.total_amount,
        cart_items_count: freshCart.total_items
      }
    }

    const config = {}

    // Add session key for anonymous users
    if (sessionKey) {
      config.data = { session_key: sessionKey }
    }

    const response = await axios.delete(`${CART_BASE}/items/${itemId}/remove/`, config)
    return normalizeRemoveResponse(response.data)
  } catch (error) {
    // console.error('❌ Error removing item from cart:', error.response?.data || error)
    throw error
  }
}

// Clear Cart
export const clearCartOnBackend = async (sessionKey = null) => {
  try {
    const config = {}
    if (!isAuthenticated()) sessionKey = sessionKey || getOrCreateSessionKey()
    if (sessionKey) config.data = { session_key: sessionKey }
    const response = await axios.delete(`${CART_BASE}/clear/`, config)
    return normalizeGenericCartResponse(response.data)
  } catch (error) {
    // console.error('Error clearing cart:', error)
    throw error
  }
}

// Get Cart Contents
export const getCartFromBackend = async (sessionKey = null) => {
  try {
    // For authenticated users, check token
    const isAuth = isAuthenticated()

    // Always get/create session key for non-authenticated users
    if (!isAuth) {
      sessionKey = sessionKey || getOrCreateSessionKey()
    } else {
    }

    const params = sessionKey ? { session_key: sessionKey } : {}

    const response = await axios.get(`${CART_BASE}/`, { params })

    return normalizeCartResponse(response.data)
  } catch (error) {
    // console.error('❌ Error fetching cart:', error.response?.data || error)
    throw error
  }
}

// ---------------- Normalizers ----------------
function normalizeCartResponse(data) {
  if (!data) return null
  return {
    id: data.id || null,
    vendor: data.vendor || null,
    items: (data.items || []).map(mapItem),
    total_amount: data.total_amount || 0,
    total_items: data.total_items || 0,
    // Promotion / coupon level fields from backend (used by CartSection)
    has_promotions: data.has_promotions ?? false,
    original_total_amount: data.original_total_amount ?? null,
    total_savings: data.total_savings ?? null,
    product_promotion_savings: data.product_promotion_savings ?? null,
    // Applied promo code information
    applied_promo_code: data.applied_promo_code || null,
    promo_discount_amount: data.promo_discount_amount || null,
    promo_code_info: data.promo_code_info
      ? {
          code: data.promo_code_info.code,
          discount_type: data.promo_code_info.discount_type,
          discount_value: data.promo_code_info.discount_value,
          discount_display: data.promo_code_info.discount_display,
          discount_amount: data.promo_code_info.discount_amount,
          is_free_delivery: data.promo_code_info.is_free_delivery ?? false
        }
      : null,
    created_at: data.created_at,
    updated_at: data.updated_at
  }
}

function normalizeAddOrUpdateResponse(data) {
  if (!data) return null

  // Check if response is a full cart object (backend returns cart directly)
  if (data.items && Array.isArray(data.items)) {
    return {
      success: true,
      message: data.message || 'Operation successful',
      cart: normalizeCartResponse(data),
      cart_total: data.total_amount || 0,
      cart_items_count: data.total_items || 0
    }
  }

  // Standard response with cart_item
  return {
    success: data.success === true,
    message: data.message,
    cart_item: data.cart_item ? mapItem(data.cart_item) : null,
    cart_total: data.cart_total ?? data.cart_total_amount ?? 0,
    cart_items_count: data.cart_items_count ?? data.items_count ?? null
  }
}

function normalizeRemoveResponse(data) {
  if (!data) return null
  return {
    success: data.success === true,
    message: data.message,
    cart_total: data.cart_total ?? 0,
    cart_items_count: data.cart_items_count ?? 0
  }
}

function normalizeGenericCartResponse(data) {
  if (!data) return null
  return {
    success: data.success === true,
    message: data.message,
    cart_total: data.cart_total ?? 0,
    cart_items_count: data.cart_items_count ?? 0
  }
}

function mapItem(item) {
  if (!item) return null


  const product = item.product || {}
  const variant = item.variant || null
  const unitPrice = item.unit_price || variant?.final_price || product.price || 0

  // Extract item ID from various possible fields
  const itemId = item.id || item.cart_item_id || item.item_id || item.pk

  // Extract product ID from various possible fields
  const productId = product.id || product.product_id || product.pk || item.product_id


  // Generate fallback ID only if absolutely necessary
  const finalItemId = itemId || `temp-${productId || 'unknown'}-${Date.now()}`

  return {
    id: finalItemId,
    product: {
      id: productId,
      name: product.name,
      description: product.description,
      image: product.image,
      price: product.price,
      vendor_id: product.vendor_id || product.vendor,
      max_order_quantity: product.max_order_quantity,
      // What the cart must warn about before checkout (utils/cartIntegrity).
      is_available: product.is_available,
      status: product.status,
      stock_quantity: product.stock_quantity,
      unit: product.unit
    },
    // Promotion / coupon fields per item (used by CartSection for badges and original prices)
    has_promotion: item.has_promotion ?? product.has_promotion ?? false,
    promotion_badge: item.promotion_badge || product.promotion_badge_text || null,
    original_unit_price: item.original_unit_price ?? null,
    original_total_price: item.original_total_price ?? null,
    // Keep both the full variant object (for UI) and a flat variant_id (for cart logic)
    variant_id: variant?.id || item.variant_id || item.variantId || null,
    variant: variant
      ? {
          id: variant.id,
          name: variant.name,
          value: variant.value,
          price_adjustment: variant.price_adjustment,
          final_price: variant.final_price,
          inventory_quantity: variant.inventory_quantity
        }
      : null,
    quantity: item.quantity,
    unit_price: unitPrice,
    total_price: item.total_price ?? unitPrice * (item.quantity || 1),
    special_instructions: item.special_instructions || '',
    created_at: item.created_at
  }
}

// Merge anonymous cart into the authenticated user's cart after login.
// Sends the actual items array (frontend's definitive copy) plus session_key as fallback.
export const mergeAnonymousCart = async (cartItems = []) => {
  const sessionKey = localStorage.getItem('cart_session_key')
  if (!sessionKey && cartItems.length === 0) return null

  const payload = {}
  if (sessionKey) payload.session_key = sessionKey
  if (cartItems.length > 0) {
    payload.items = cartItems.map(item => ({
      product_id: item.product?.id || item.product_id,
      variant_id: item.variant_id || item.variant?.id || null,
      quantity: item.quantity || 1,
      special_instructions: item.special_instructions || ''
    }))
  }

  try {
    const response = await axios.post(`${CART_BASE}/merge/`, payload)
    localStorage.removeItem('cart_session_key')
    return normalizeCartResponse(response.data?.cart || response.data)
  } catch (error) {
    localStorage.removeItem('cart_session_key')
    throw error
  }
}

// Utility to safely extract product info (used by UI fallbacks)
export const getProductDataFromCartItem = (cartItem, fallbackId = null) => {
  if (!cartItem) return { id: fallbackId, name: 'Unknown Product', price: 0, max_order_quantity: 999 }
  const p = cartItem.product || {}
  return {
    id: p.id || fallbackId,
    name: p.name || 'Unknown Product',
    price: cartItem.unit_price || cartItem.total_price || p.price || 0,
    max_order_quantity: p.max_order_quantity || 999
  }
}
